import {
  Autocomplete,
  CircularProgress,
  Dialog,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import IconButton from "@mui/material/IconButton";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { MuiColorInput } from "mui-color-input";
import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import { PageGroupEndPointLogic } from "../../../../../../logic/AssetManagement/PageGroups/pageGroupEndPointLogic";

interface Props {
  websiteShareuid: any;
  oauthTokenHeader: any;
  siteAllAvailableWidgets: any;
  handleChange: any;
  webSiteValues: any;
  designThemeData: any;
  formError: any;
}
function DefaultAppearance({
  siteAllAvailableWidgets,
  designThemeData,
  webSiteValues,
  handleChange,
  websiteShareuid,
  oauthTokenHeader,
  formError,
}: Props) {
  const [imagePath, setImagePath] = useState("");
  const [loading, setLoading] = useState(false);

  const [uploadingDevice, setUploadingDevice] = useState("");

  const [bannerImagePathDesktop, setBannerImagePathDesktop] = useState("");
  const [bannerImagePathMobile, setBannerImagePathMobile] = useState("");
  const [bannerImagePathTablet, setBannerImagePathTablet] = useState("");
  const [base64Images, setBase64Images] = useState<{
    [key: string]: string | null;
  }>({
    desktop: null,
    mobile: null,
    tablet: null,
  }) as any;

  const imageUploadData = {
    data: {
      base64_image:
        uploadingDevice === "desktop"
          ? base64Images.desktop?.base64
          : uploadingDevice === "mobile"
          ? base64Images.mobile?.base64
          : base64Images.tablet?.base64,
      file_name:
        uploadingDevice === "desktop"
          ? base64Images.desktop?.fileName
          : uploadingDevice === "mobile"
          ? base64Images.mobile?.fileName
          : base64Images.tablet?.fileName,
    },
  };

  const handleInputChange = (name: string, value: any) => {
    handleChange(name, value);
  };

  const handleFileUpload = (
    device: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBase64Images((prevState: any) => ({
          ...prevState,
          [device]: {
            base64: reader.result as string, // Store the base64 image
            fileName: file.name, // Store the file name
          },
        }));
      };
      reader.readAsDataURL(file);
    } else {
      alert("Please upload a valid image file.");
    }
  };

  //Loading Modal
  const [open, setOpen] = useState(true);
  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (uploadingDevice === "desktop") {
      setBannerImagePathDesktop(imagePath);
    }
    if (uploadingDevice === "mobile") {
      setBannerImagePathMobile(imagePath);
    }
    if (uploadingDevice === "tablet") {
      setBannerImagePathTablet(imagePath);
    }
    // eslint-disable-next-line
  }, [imagePath, uploadingDevice]);

  useEffect(() => {
    if (webSiteValues?.banner_desktop) {
      setBannerImagePathDesktop(webSiteValues.banner_desktop);
    }
    if (webSiteValues?.banner_mobile) {
      setBannerImagePathMobile(webSiteValues.banner_mobile);
    }
    if (webSiteValues?.banner_tablet) {
      setBannerImagePathTablet(webSiteValues.banner_tablet);
    }
    // eslint-disable-next-line
  }, [webSiteValues]);

  useEffect(() => {
    if (bannerImagePathDesktop) {
      handleChange("banner_desktop", bannerImagePathDesktop);
    }
    if (bannerImagePathMobile) {
      handleChange("banner_mobile", bannerImagePathMobile);
    }
    if (bannerImagePathTablet) {
      handleChange("banner_tablet", bannerImagePathTablet);
    }
    // eslint-disable-next-line
  }, [bannerImagePathDesktop, bannerImagePathMobile, bannerImagePathTablet]);

  useEffect(() => {
    const uploadImage = async () => {
      if (uploadingDevice && base64Images[uploadingDevice]?.base64) {
        try {
          setLoading(true);
          await PageGroupEndPointLogic.postImageUpload(
            oauthTokenHeader,
            websiteShareuid,
            setImagePath,
            imageUploadData
          );
        } catch (error) {
          console.error("Image upload failed:", error);
        } finally {
          setLoading(false);
          setBase64Images((prevState: any) => ({
            ...prevState,
            [uploadingDevice]: null, // Reset only the uploaded image
          }));
        }
      }
    };
    uploadImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base64Images, uploadingDevice]);

  //-- Get Popup Offer Widget
  const filteredWidgets =
    siteAllAvailableWidgets?.filter(
      (widget: any) => widget?.type === "Popup Offer"
    ) || [];

  const filteredWidgetsNoActive =
    siteAllAvailableWidgets?.filter(
      (widget: any) => widget?.type === "Popup Offer" && widget?.active
    ) || [];

  return (
    <>
      {loading && (
        <Dialog open={open} onClose={handleClose}>
          <Paper sx={{ padding: "16px 24px 20px 24px" }}>
            <Stack alignItems="center" justifyContent="center">
              <CircularProgress color="secondary" />
            </Stack>
          </Paper>
        </Dialog>
      )}
      <Typography>Default appearance</Typography>
      <Box mt={1} mb={2}>
        <Grid container columnSpacing={{ xs: 2, sm: 2, md: 2 }}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              options={
                designThemeData.length > 0
                  ? designThemeData.map((theme: any) => ({
                      name: theme?.name,
                      shared_uid: theme?.shared_uid,
                    }))
                  : []
              }
              getOptionLabel={(option: any) => option.name || ""}
              value={
                designThemeData.find(
                  (theme: any) =>
                    theme.shared_uid === webSiteValues?.default_theme_shared_uid
                ) || null
              }
              onChange={(_event, newValue: any) => {
                handleInputChange(
                  "default_theme_shared_uid",
                  newValue?.shared_uid || ""
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Theme"
                  name={"default_theme_shared_uid"}
                  error={formError?.default_theme_shared_uid}
                  helperText={formError?.default_theme_shared_uid}
                  color="secondary"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                />
              )}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              options={
                siteAllAvailableWidgets.length > 0
                  ? filteredWidgetsNoActive.map((widget: any) => ({
                      widget_name: widget?.widget_name,
                      shared_uid: widget?.shared_uid,
                    }))
                  : []
              }
              getOptionLabel={(option: any) => option.widget_name || ""}
              value={
                siteAllAvailableWidgets.length > 0
                  ? filteredWidgets.find(
                      (widget: any) =>
                        widget.shared_uid ===
                        webSiteValues?.default_popup_shared_uid
                    ) || null
                  : null
              }
              onChange={(_event, newValue: any) => {
                handleInputChange(
                  "default_popup_shared_uid",
                  newValue?.shared_uid || ""
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Popup"
                  name={"default_popup_shared_uid"}
                  error={formError?.default_popup_shared_uid}
                  helperText={formError?.default_popup_shared_uid}
                  color="secondary"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                />
              )}
              size="small"
            />
          </Grid>
        </Grid>

        <Grid container columnSpacing={{ xs: 2, sm: 2, md: 2 }} mt={2}>
          <Grid item xs={12} md={6}>
            <MuiColorInput
              format="hex"
              name={"cta_button_color"}
              value={webSiteValues?.cta_button_color}
              onChange={(newValue: any) =>
                handleInputChange("cta_button_color", newValue)
              }
              error={formError?.cta_button_color}
              helperText={formError?.cta_button_color}
              size="small"
              color="secondary"
              label="CTA Button color"
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              name={"cta_button_text"}
              fullWidth
              required
              value={webSiteValues?.cta_button_text}
              onChange={(e) =>
                handleInputChange("cta_button_text", e.target.value)
              }
              error={formError?.cta_button_text}
              helperText={formError?.cta_button_text}
              id="outlined-required"
              label="CTA Button text"
              InputLabelProps={{
                shrink: true,
              }}
              color="secondary"
            />
          </Grid>
        </Grid>

        {["desktop", "mobile", "tablet"].map((device) => (
          <Grid
            key={device}
            container
            columnSpacing={{ xs: 2, sm: 2, md: 2 }}
            mt={2}
          >
            <Grid item xs={12} md={11}>
              <TextField
                size="small"
                fullWidth
                required
                error={formError?.[`banner_${device}`]} //-- red border if empty
                helperText={formError?.[`banner_${device}`]}
                id={`outlined-required-${device}`}
                label={`${
                  device.charAt(0).toUpperCase() + device.slice(1)
                } Banner Image`}
                //onChange={(e) => handleTextChange(device, e.target.value)}
                value={
                  device === "desktop"
                    ? bannerImagePathDesktop
                    : device === "mobile"
                    ? bannerImagePathMobile
                    : bannerImagePathTablet
                }
                InputLabelProps={{
                  shrink: true,
                }}
                color="secondary"
                disabled={true}
              />
            </Grid>
            <Grid item xs={1} md={1}>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id={`file-upload-${device}`}
                type="file"
                onChange={(e) => {
                  setUploadingDevice(device);
                  handleFileUpload(device, e);
                }}
              />
              <label htmlFor={`file-upload-${device}`}>
                <IconButton aria-label="delete" component="span">
                  <FileUploadOutlinedIcon color="secondary" />
                </IconButton>
              </label>
            </Grid>
          </Grid>
        ))}

        <Grid container columnSpacing={{ xs: 2, sm: 2, md: 2 }} mt={2}>
          <Grid item>
            <Typography
              sx={{ marginTop: "4px", fontSize: "12px", color: "#00000099" }}
            >
              All fields are mandatory.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default DefaultAppearance;
